// Centro de notificaciones — ADR-2026-08-31-centro-de-notificaciones.
//
// Como en el programa de referidos (ADR-2026-08-27-programa-referidos), el peso
// está en la base: las notificaciones las escriben TRIGGERS de Postgres, no el
// front. Acá sólo hay tres RPC de lectura/marcado y el mapeo de cada tipo a su
// presentación (ícono y color).
//
// En particular NO hay ninguna función para CREAR una notificación: `notificaciones_crear`
// existe en la base pero tiene el EXECUTE revocado para `authenticated`. Si el
// navegador pudiera escribir, una notificación dependería de que la pestaña esté
// abierta en el momento justo — que es exactamente el caso que esta feature existe
// para cubrir.

import { supabase } from './supabase';
import { SUPABASE_URL } from '../config';

/**
 * Los tipos que hoy escriben los triggers. El front NO valida contra esta lista:
 * `presentacionNotificacion` cae a un default para cualquier tipo desconocido, así
 * que se pueden agregar orígenes nuevos en la base sin desplegar el front — la
 * notificación se ve con el ícono genérico hasta que alguien le dé el suyo.
 */
export type TipoNotificacion =
  // Con origen escrito (triggers de Postgres).
  | 'imagen_lista'
  | 'video_listo'
  | 'generacion_error'
  | 'referido_activado'
  // Los escribe atlas-backend al terminar una importación masiva (Mercado Libre, después
  // Tiendanube): ADR-2026-09-29-importacion-masiva-front.
  | 'importacion_terminada'
  | 'importacion_fallida'
  // Sin origen todavía: la presentación ya está definida para que el día que se
  // enganchen no haya que tocar el front y no se vean con la campana genérica.
  | 'creditos_renovados'
  | 'creditos_agotados'
  | 'tiendanube_import'
  | 'tiendanube_desconectada'
  | 'pago_fallido'
  | 'renovacion_proxima'
  | 'calendario_listo'
  | 'voz_lista'
  | 'tiendanube_export'
  | 'novedad'
  | (string & {});

export interface Notificacion {
  id: string;
  tipo: TipoNotificacion;
  titulo: string;
  cuerpo: string | null;
  /** Ruta interna a la que lleva el tap. `null` = la notificación no es accionable. */
  ruta: string | null;
  imagen_url: string | null;
  /** Payload libre del origen. Para las generaciones trae `cantidad` y `file_name`. */
  datos: Record<string, unknown>;
  created_at: string;
  leida: boolean;
}

// ── Lo que terminó mientras estabas en Atlas no suma el puntito ───────────────────────────────
//
// Pedido de Nacho (5/10): si generás estando en Atlas, la imagen la ves en pantalla y el puntito
// rojo de "Tu imagen ya está lista" sobra —medido: el 96% de esos avisos nunca se leía—. Sólo
// tiene sentido si te fuiste (otra pestaña, otra app, cerraste) mientras se generaba.
//
// La campana guarda qué había al llegar; lo de generación que aparece DESPUÉS, mientras seguís en
// Atlas, se marca leído solo: queda en la lista como historial, sin puntito. Se compara por id y no
// por hora para no depender del reloj de la computadora, que puede estar corrido.

/** Los avisos que se silencian si terminaron con la persona en Atlas. Los demás no se tocan. */
export const TIPOS_QUE_SE_VEN_EN_PANTALLA: ReadonlySet<TipoNotificacion> = new Set<TipoNotificacion>([
  'imagen_lista',
  'video_listo',
  'generacion_error',
]);

/**
 * Qué marcar leído en esta vuelta de la campana, y qué recordar para la próxima.
 *
 * - No está en Atlas → nada, y se olvida lo que había: al volver se arma de nuevo, así lo que
 *   termine mientras no está queda SIN leer.
 * - Recién llega (`vistas` en null) → no se marca nada: todo lo que hay terminó antes. Se recuerda.
 * - Ya estaba → se marca lo de generación, sin leer, que no estaba la vuelta anterior.
 */
export function silenciarLoVistoEnPantalla(
  items: Notificacion[],
  vistas: ReadonlySet<string> | null,
  enAtlas: boolean,
): { silenciar: string[]; vistas: Set<string> | null } {
  if (!enAtlas) return { silenciar: [], vistas: null };
  const ahora = new Set(items.map((n) => n.id));
  if (vistas === null) return { silenciar: [], vistas: ahora };
  const silenciar = items
    .filter((n) => !n.leida && TIPOS_QUE_SE_VEN_EN_PANTALLA.has(n.tipo) && !vistas.has(n.id))
    .map((n) => n.id);
  for (const id of vistas) ahora.add(id);
  return { silenciar, vistas: ahora };
}

/**
 * ¿La persona estuvo en Atlas durante TODO un refresco de la campana? Sólo así la lista que trajo
 * sirve para decidir qué vio en pantalla.
 *
 * Un refresco que empezó con la persona afuera y terminó con ella adentro trae una lista VIEJA: si
 * se tomara como "lo que había al llegar", un aviso que terminó mientras estaba afuera parecería
 * nuevo y se marcaría leído sin que lo viera (review #157). `vuelta` cuenta cada entrada o salida
 * de Atlas: si cambió durante el refresco, la lista no vale y la arma el refresco siguiente.
 */
export function estuvoEnAtlasTodoElRefresco(
  alEmpezar: { enAtlas: boolean; vuelta: number },
  alTerminar: { enAtlas: boolean; vuelta: number },
): boolean {
  return alEmpezar.enAtlas && alTerminar.enAtlas && alEmpezar.vuelta === alTerminar.vuelta;
}

/** Una página de la lista. `cursor` es lo que hay que pasar como `antes` para seguir. */
export interface PaginaNotificaciones {
  items: Notificacion[];
  /** `null` cuando no hay más para traer. */
  cursor: string | null;
}

/** Cuántas trae el panel del popover. La pantalla completa pide de a más. */
export const TOPE_PANEL = 8;
export const TOPE_PAGINA = 20;

/**
 * Trae una página de notificaciones, de la más nueva a la más vieja.
 *
 * La paginación va por CURSOR (`antes`, un timestamp) y no por offset: la lista
 * crece por arriba, así que con offset una notificación que entra entre dos pedidos
 * repite o saltea filas.
 */
export async function cargarNotificaciones(
  { limite = TOPE_PAGINA, antes = null }: { limite?: number; antes?: string | null } = {},
): Promise<PaginaNotificaciones> {
  const { data, error } = await supabase.rpc('notificaciones_listar', {
    p_limite: limite,
    p_antes: antes,
  });
  if (error) throw error;

  const items = (data ?? []) as Notificacion[];
  return {
    items,
    // Si vino una página completa asumimos que hay más. Pedir una de más sólo para
    // saberlo duplicaría las consultas de la pantalla; el costo de equivocarse es
    // un "Cargar más" que trae cero filas y se apaga solo.
    cursor: items.length === limite ? (items[items.length - 1]?.created_at ?? null) : null,
  };
}

/**
 * Cuántas hay sin leer. Va en su propia RPC porque es lo único que necesita el
 * badge, y el badge se refresca mucho más seguido que la lista: no hay razón para
 * traer 20 filas cada vez que hay que dibujar un número.
 */
export async function contarSinLeer(): Promise<number> {
  const { data, error } = await supabase.rpc('notificaciones_sin_leer');
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

/**
 * Marca como leídas. Sin `ids` marca TODAS las visibles ("Marcar todas como leídas").
 *
 * "Leída" se guarda POR USUARIO en una tabla aparte: en una cuenta con dos empleados,
 * si fuera una columna de la notificación, el primero que abre la campana le apagaría
 * el badge al otro.
 */
export async function marcarLeidas(ids?: string[]): Promise<void> {
  const { error } = await supabase.rpc('notificaciones_marcar_leidas', {
    p_ids: ids && ids.length > 0 ? ids : null,
  });
  if (error) throw error;
}

/** Cómo se dibuja cada tipo: glifo de Material Symbols y color del casillero. */
export interface PresentacionNotificacion {
  icono: string;
  /** Clases del casillero de 36px que envuelve al ícono. */
  clases: string;
}

// El COLOR clasifica por urgencia, no por origen. Tres niveles y nada más:
//   · azul   — algo terminó bien y hay contenido nuevo para mirar;
//   · ámbar  — algo no salió, pero no hay nada que el usuario deba hacer;
//   · rojo   — el servicio se corta si no hace algo (un cobro, una integración caída).
// El ícono es lo que dice DE QUÉ se trata. Si el color también intentara decirlo,
// nueve tipos serían nueve colores y ninguno significaría nada.
const AZUL = 'bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary';
const AMBAR = 'bg-[#FEF3E2] text-[#B45309]';
const ROJO = 'bg-[#FDECEC] text-[#B91C1C]';

const PRESENTACIONES: Record<string, PresentacionNotificacion> = {
  imagen_lista: { icono: 'photo_library', clases: AZUL },
  // `play_circle` y no `movie`: con FILL 1 la claqueta es un rectángulo lleno y a
  // 19px queda igual que cualquier otro glifo rectangular de la lista (`inventory_2`,
  // `storefront`). El círculo es la única silueta de la lista que no se confunde.
  video_listo: { icono: 'play_circle', clases: AZUL },
  // Ámbar y no rojo: la generación falló pero no se cobraron los créditos, así que
  // no hay nada que el usuario tenga que ir a arreglar.
  generacion_error: { icono: 'error', clases: AMBAR },
  // Verde, el único fuera de la escala: es la única notificación que trae una buena
  // noticia con plata adentro, y perderla entre los azules sería desperdiciarla.
  referido_activado: { icono: 'redeem', clases: 'bg-[#E8F7EE] text-[#15803D]' },

  // Créditos: el mismo glifo para los dos estados y el color como única diferencia.
  // Son el mismo dato —cuántos te quedan— en dos momentos, y cambiarle el ícono
  // haría parecer que hablan de cosas distintas.
  creditos_renovados: { icono: 'bolt', clases: AZUL },
  creditos_agotados: { icono: 'bolt', clases: AMBAR },

  tiendanube_import: { icono: 'inventory_2', clases: AZUL },
  // La importación masiva es la misma idea que la de Tienda Nube: el mismo glifo.
  importacion_terminada: { icono: 'inventory_2', clases: AZUL },
  // Ámbar y no rojo: lo que llegó quedó en Atlas y "Buscar productos nuevos" retoma el resto.
  importacion_fallida: { icono: 'error', clases: AMBAR },
  // Roja: mientras esté desconectada, la tienda deja de sincronizar.
  tiendanube_desconectada: { icono: 'link_off', clases: ROJO },

  // `credit_card` y no `credit_card_off`: con el relleno de la familia (FILL 1), el
  // glifo tachado es un rectángulo sólido cruzado por una diagonal y a 19px se lee
  // como un bloque de color, no como una tarjeta. El "no se pudo" ya lo dicen el
  // título y el rojo; el ícono sólo tiene que decir "tarjeta".
  pago_fallido: { icono: 'credit_card', clases: ROJO },

  calendario_listo: { icono: 'event_available', clases: AZUL },
  voz_lista: { icono: 'graphic_eq', clases: AZUL },
  // `sell` (etiqueta de precio, en diagonal) y no `storefront`: la tienda rellena es
  // otro rectángulo, y quedaba a dos filas de `inventory_2` —la importación— siendo
  // el par que MÁS necesita distinguirse, porque las dos hablan de Tienda Nube.
  tiendanube_export: { icono: 'sell', clases: AZUL },
  // Violeta, fuera de la escala de urgencia: una novedad de producto no es ni un
  // resultado que el usuario estaba esperando ni un problema. Si fuera azul se
  // mezclaría con "tus imágenes están listas", que es lo que la gente viene a buscar.
  novedad: { icono: 'auto_awesome', clases: 'bg-[#F1ECFE] text-[#6D28D9]' }, // hex-de-marca-ok: color propio del tipo de aviso, va con su texto
  // Ámbar y no roja: todavía no pasó nada: es un aviso con tiempo de sobra.
  renovacion_proxima: { icono: 'event_upcoming', clases: AMBAR },
};

const PRESENTACION_DEFAULT: PresentacionNotificacion = {
  icono: 'notifications',
  clases: 'bg-black/[0.05] text-black/55',
};

function hostDe(url: string | undefined): string {
  try {
    return url ? new URL(url).host : '';
  } catch {
    return '';
  }
}

/**
 * La imagen propia de un aviso (la de una novedad manual, `notificaciones.imagen_url`), si es una
 * que se puede mostrar: una ruta de la app (`/images/...`) o un https de estudio-atlas.com o del
 * Storage de Atlas, la misma regla que `imagenDeOrigenPermitido` del backend. Cualquier otra cosa
 * —otro sitio, http, `//otro.com`, `data:`— no se dibuja y la fila queda con el ícono del tipo:
 * abrir la campana no le pide nada a un servidor ajeno.
 * ADR-2026-11-02-imagen-en-el-aviso-de-la-campana.
 */
export function imagenDeLaCampana(
  url: string | null | undefined,
  hostDeSupabase: string = hostDe(SUPABASE_URL),
): string | null {
  const u = (url ?? '').trim();
  if (!u) return null;
  if (u.startsWith('/') && !u.startsWith('//') && !/[\s\\\x00-\x1f]/.test(u)) return u;
  let parsed: URL;
  try {
    parsed = new URL(u);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password) return null;
  const { host } = parsed;
  const deAtlas = host === 'estudio-atlas.com' || host.endsWith('.estudio-atlas.com');
  return deAtlas || (hostDeSupabase !== '' && host === hostDeSupabase) ? u : null;
}

/** Nunca tira: un tipo que el front no conoce se dibuja con la campana genérica. */
export function presentacionNotificacion(tipo: string): PresentacionNotificacion {
  return PRESENTACIONES[tipo] ?? PRESENTACION_DEFAULT;
}

/**
 * "hace 5 min", "hace 3 h", "ayer", "12 ago".
 *
 * Corto a propósito: en una fila de lista el tiempo es el dato menos importante y
 * compite por el ancho con el título. Arriba de una semana pasa a fecha absoluta —
 * "hace 23 días" obliga a hacer la cuenta y no dice más que "8 ago".
 */
export function tiempoRelativo(iso: string, ahora: Date = new Date()): string {
  const fecha = new Date(iso);
  const seg = Math.round((ahora.getTime() - fecha.getTime()) / 1000);

  // El reloj del cliente puede ir atrasado respecto del de la base: sin esto, una
  // notificación recién creada muestra "hace -3 s".
  if (seg < 60) return 'recién';
  const min = Math.round(seg / 60);
  if (min < 60) return `hace ${min} min`;

  // De acá para arriba se cuenta por DÍAS DE CALENDARIO y no por horas corridas, para
  // que esto no se contradiga con `grupoDeFecha`, que agrupa por calendario.
  //
  // El caso que lo rompía: algo de las 20:48 mirado a las 00:48. Son 4 horas, pero es
  // el día anterior — y la pantalla mostraba el encabezado "AYER" con la fila diciendo
  // "hace 4 h" abajo. Las dos cuentas eran correctas por separado y juntas se leían
  // como un error. Manda el calendario: es lo que agrupa la lista, y es además cómo
  // habla la gente ("eso fue ayer", aunque hayan pasado cuatro horas).
  const dias = diasDeCalendario(fecha, ahora);
  if (dias === 0) return `hace ${Math.round(min / 60)} h`;
  if (dias === 1) return 'ayer';
  if (dias < 7) return `hace ${dias} días`;
  return fecha.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

/** Días de calendario entre dos fechas, ignorando la hora. */
function diasDeCalendario(fecha: Date, ahora: Date): number {
  const dia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((dia(ahora) - dia(fecha)) / 86400000);
}

/**
 * Rótulo del grupo al que pertenece — lo usa la pantalla completa, que separa por
 * día como hacen las apps de referencia (Linktree, Beli, Strava en Mobbin). El
 * popover NO agrupa: con 8 filas los encabezados ocupan más que el contenido.
 */
export function grupoDeFecha(iso: string, ahora: Date = new Date()): string {
  // Misma cuenta que `tiempoRelativo`: las dos tienen que decir lo mismo o la fila
  // contradice a su propio encabezado.
  const dias = diasDeCalendario(new Date(iso), ahora);
  if (dias <= 0) return 'Hoy';
  if (dias === 1) return 'Ayer';
  if (dias < 7) return 'Esta semana';
  if (dias < 30) return 'Este mes';
  return 'Más antiguas';
}

// ─── Marcar leída la que se tocó desde el celular ────────────────────────────
//
// El push nace de una notificación de la campana (el cron del backend la lee para armar el aviso),
// así que tocarlo es haberla visto. Si no se marca, la persona se entera por el celular y el punto
// rojo le sigue insistiendo con algo que ya vio.
//
// El id llega por DOS caminos, porque hay dos situaciones:
//  - App CERRADA → se abre una pestaña nueva y el id viene en la URL.
//  - App ABIERTA → la pestaña no vuelve a arrancar, así que el service worker avisa por postMessage.
// Los dos terminan en la misma cola.

export const PARAM_NOTIF_LEIDA = 'notif_leida';
const CLAVE_PENDIENTES = 'atlas:notif-leidas-pendientes';

/**
 * Cola en sessionStorage y no marcado directo: cuando el push abre la app, la sesión de Supabase
 * todavía no está lista, y `marcarLeidas` sin sesión falla. Guardarlo y marcarlo cuando haya sesión
 * es lo que hace que el caso más común —abrir desde el celular con la app cerrada— funcione.
 *
 * Todo va en try/catch: en navegación privada el solo hecho de tocar sessionStorage puede tirar.
 */
function pendientes(): string[] {
  try {
    const crudo = sessionStorage.getItem(CLAVE_PENDIENTES);
    const lista = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(lista) ? lista.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Anota una notificación como "tocada desde el push", para marcarla cuando haya sesión. */
export function anotarNotificacionTocada(id: string): void {
  if (!id) return;
  try {
    const ids = pendientes();
    if (!ids.includes(id)) sessionStorage.setItem(CLAVE_PENDIENTES, JSON.stringify([...ids, id]));
  } catch {
    /* sin sessionStorage no se puede recordar: se pierde el marcado, no la navegación */
  }
}

/**
 * Levanta el id de la URL (app abierta desde un push) y LIMPIA el parámetro.
 *
 * Limpiarlo importa: si quedara, recargar la página volvería a marcarla —inofensivo pero inútil— y
 * sobre todo la URL se comparte y se guarda en favoritos con un id de notificación colgado.
 */
export function capturarNotificacionDelPush(): void {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get(PARAM_NOTIF_LEIDA);
    if (!id) return;
    anotarNotificacionTocada(id);
    url.searchParams.delete(PARAM_NOTIF_LEIDA);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    /* URL rara: no vale la pena romper el arranque de la app por esto */
  }
}

/**
 * Marca las que quedaron pendientes. Devuelve true si marcó algo, para que quien llame refresque
 * la lista y el contador.
 *
 * No lanza: esto es un detalle de comodidad; si falla, la notificación queda sin leer y la persona
 * la marca a mano. Romper la campana por esto sería mucho peor.
 */
export async function marcarPendientesDelPush(): Promise<boolean> {
  const ids = pendientes();
  if (ids.length === 0) return false;
  try {
    // LA SESIÓN SE PREGUNTA ANTES, y no es un chequeo de más: la RPC `notificaciones_marcar_leidas`
    // devuelve 0 **sin error** cuando no hay sesión (`if v_user is null then return 0`). Sin esto,
    // llamarla sin sesión parecía haber funcionado, se vaciaba la cola y el marcado se perdía para
    // siempre — justo el caso que la cola existe para cubrir: el push abre la app y Supabase todavía
    // no levantó la sesión.
    const { data } = await supabase.auth.getSession();
    if (!data.session) return false; // queda en la cola para cuando haya sesión

    await marcarLeidas(ids);
    // Se sacan SOLO los que se marcaron, no se vacía la cola: entre el snapshot de arriba y este
    // punto pasaron dos `await` (la sesión y la RPC), y en ese rato pudo entrar otro tap. Vaciar
    // borraría ese id sin haberlo marcado y sin dejar nada pendiente — se perdería en silencio.
    // Pasa de verdad: el service worker avisa por mensaje Y navega con el parámetro, así que dos
    // llamadas solapadas son lo normal, no el caso raro.
    try {
      const restantes = pendientes().filter((id) => !ids.includes(id));
      if (restantes.length > 0) sessionStorage.setItem(CLAVE_PENDIENTES, JSON.stringify(restantes));
      else sessionStorage.removeItem(CLAVE_PENDIENTES);
    } catch {
      /* si no se puede limpiar, se reintenta en la próxima carga: marcar dos veces es inocuo */
    }
    return true;
  } catch {
    return false; // queda en la cola para el próximo intento
  }
}

/**
 * Escucha al service worker para el caso "la app YA estaba abierta": ahí la pestaña no vuelve a
 * arrancar, así que el parámetro de la URL no alcanza.
 *
 * Devuelve la función para dejar de escuchar.
 */
export function escucharTapDelPush(alTocar: (id: string) => void): () => void {
  if (!('serviceWorker' in navigator)) return () => {};
  const handler = (ev: MessageEvent) => {
    const d = ev.data as { tipo?: string; id?: string } | null;
    if (d?.tipo === 'notificacion-tocada' && typeof d.id === 'string' && d.id) {
      anotarNotificacionTocada(d.id);
      alTocar(d.id);
    }
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}
